import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Member } from '../types';
import {
  User,
  Search,
  Flag,
  ChevronRight,
  AlertTriangle,
  Calendar,
  Phone,
  Mail,
  X,
} from 'lucide-react';

export default function Members() {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      const { data, error } = await supabase
        .from('members')
        .select('*')
        .order('risk_score', { ascending: false });

      if (error) throw error;
      setMembers(data || []);
    } catch (error) {
      console.error('Error fetching members:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRiskBadgeColor = (riskBand: string) => {
    switch (riskBand) {
      case 'Critical':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'High':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'Medium':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      default:
        return 'bg-green-100 text-green-800 border-green-200';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const filteredMembers = members.filter((member) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      member.first_name.toLowerCase().includes(searchLower) ||
      member.last_name.toLowerCase().includes(searchLower) ||
      member.member_id.toLowerCase().includes(searchLower) ||
      member.program?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading members...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full">
      {/* Members List */}
      <div className={`flex-1 ${selectedMember ? 'mr-96' : ''} transition-all`}>
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Member Directory
          </h1>
          <p className="text-gray-600">
            Browse and manage all members in your care network
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by name, ID, or program..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Members Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Member
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Program
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Risk
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Care Manager
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Flags
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredMembers.map((member) => (
                <tr
                  key={member.id}
                  onClick={() => setSelectedMember(member)}
                  className={`hover:bg-gray-50 cursor-pointer transition-colors ${
                    selectedMember?.id === member.id ? 'bg-purple-50' : ''
                  }`}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-purple-100 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-purple-600" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {member.first_name} {member.last_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {member.member_id}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {member.program || 'Not assigned'}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <span className="text-lg font-bold text-gray-900 mr-2">
                        {member.risk_score}
                      </span>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full border ${getRiskBadgeColor(
                          member.risk_band
                        )}`}
                      >
                        {member.risk_band}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {member.assigned_care_manager || 'Unassigned'}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-1">
                      {member.sdoh_flags.length > 0 && (
                        <span
                          className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full border border-purple-200 flex items-center"
                          title="Social Determinants of Health"
                        >
                          <Flag className="w-3 h-3 mr-1" />
                          SDoH
                        </span>
                      )}
                      {member.behavioral_health_flags.length > 0 && (
                        <span
                          className="px-2 py-1 text-xs font-medium bg-pink-100 text-pink-800 rounded-full border border-pink-200 flex items-center"
                          title="Behavioral Health"
                        >
                          <Flag className="w-3 h-3 mr-1" />
                          BH
                        </span>
                      )}
                      {!member.consent_on_file && (
                        <span
                          className="px-2 py-1 text-xs font-medium bg-amber-100 text-amber-800 rounded-full border border-amber-200 flex items-center"
                          title="No Consent"
                        >
                          <AlertTriangle className="w-3 h-3" />
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/members/${member.member_id}/comprehensive`);
                        }}
                        className="text-blue-600 hover:text-blue-900 text-sm font-medium flex items-center"
                      >
                        View 360
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredMembers.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">No members found</p>
            </div>
          )}
        </div>
      </div>

      {/* Quick View Sidebar */}
      {selectedMember && (
        <>
          {/* Backdrop Overlay */}
          <div
            className="fixed inset-0 bg-black bg-opacity-25 z-40"
            onClick={() => setSelectedMember(null)}
          />

          {/* Sidebar Panel */}
          <div className="fixed right-0 top-0 h-full w-96 bg-white border-l border-gray-200 shadow-2xl overflow-y-auto z-50">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 z-10">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start space-x-3">
                  <div className="bg-purple-100 p-2 rounded-full">
                    <User className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      {selectedMember.first_name} {selectedMember.last_name}
                    </h2>
                    <p className="text-sm text-gray-600">
                      {selectedMember.member_id}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedMember(null)}
                  className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

            <button
              onClick={() => navigate(`/members/${selectedMember.member_id}/comprehensive`)}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center"
            >
              View Member 360
              <ChevronRight className="w-4 h-4 ml-2" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Risk Score */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">
                Risk Assessment
              </h3>
              <div className="flex items-center space-x-3">
                <span className="text-3xl font-bold text-gray-900">
                  {selectedMember.risk_score}
                </span>
                <span
                  className={`px-3 py-1 text-sm font-medium rounded-full border ${getRiskBadgeColor(
                    selectedMember.risk_band
                  )}`}
                >
                  {selectedMember.risk_band} Risk
                </span>
              </div>
            </div>

            {/* Contact Information */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-3">
                Contact Information
              </h3>
              <div className="space-y-2">
                <div className="flex items-center text-sm">
                  <Mail className="w-4 h-4 text-gray-400 mr-2" />
                  <span className="text-gray-900">
                    {selectedMember.email || 'Not provided'}
                  </span>
                </div>
                <div className="flex items-center text-sm">
                  <Phone className="w-4 h-4 text-gray-400 mr-2" />
                  <span className="text-gray-900">
                    {selectedMember.phone || 'Not provided'}
                  </span>
                </div>
                <div className="flex items-start text-sm">
                  <Calendar className="w-4 h-4 text-gray-400 mr-2 mt-0.5" />
                  <div>
                    <p className="text-gray-900">
                      DOB: {formatDate(selectedMember.date_of_birth)}
                    </p>
                    {selectedMember.address && (
                      <p className="text-gray-600 mt-1">
                        {selectedMember.address}
                        {selectedMember.city && `, ${selectedMember.city}`}
                        {selectedMember.state && `, ${selectedMember.state}`}
                        {selectedMember.zip && ` ${selectedMember.zip}`}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Program */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">
                Program
              </h3>
              <p className="text-sm text-gray-900">
                {selectedMember.program || 'Not assigned'}
              </p>
            </div>

            {/* Care Manager */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">
                Care Manager
              </h3>
              <p className="text-sm text-gray-900">
                {selectedMember.assigned_care_manager || 'Unassigned'}
              </p>
            </div>

            {/* Language & Consent */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">
                Additional Information
              </h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Preferred Language:</span>
                  <span className="text-gray-900 font-medium">
                    {selectedMember.preferred_language}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Consent on File:</span>
                  <span
                    className={`font-medium ${
                      selectedMember.consent_on_file
                        ? 'text-green-600'
                        : 'text-amber-600'
                    }`}
                  >
                    {selectedMember.consent_on_file ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Status:</span>
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${
                      selectedMember.status === 'Active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {selectedMember.status}
                  </span>
                </div>
              </div>
            </div>

            {/* Flags */}
            {(selectedMember.sdoh_flags.length > 0 ||
              selectedMember.behavioral_health_flags.length > 0) && (
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">
                  Health Flags
                </h3>
                <div className="space-y-2">
                  {selectedMember.sdoh_flags.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-purple-800 mb-1">
                        Social Determinants of Health:
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {selectedMember.sdoh_flags.map((flag, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 text-xs bg-purple-50 text-purple-700 rounded border border-purple-200"
                          >
                            {flag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {selectedMember.behavioral_health_flags.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-pink-800 mb-1">
                        Behavioral Health:
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {selectedMember.behavioral_health_flags.map(
                          (flag, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 text-xs bg-pink-50 text-pink-700 rounded border border-pink-200"
                            >
                              {flag}
                            </span>
                          )
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        </>
      )}
    </div>
  );
}
